import { useCallback, memo } from 'react';
import BookingGridBase from '../common/BookingGridBase';
import BookingCardAxe from './BookingCardAxe';
import BookingCardAxev2 from './BookingCardAxev2';
import TimeLineAxe from './TimeLineAxe';

function GridAxe({
  bookings,
  rowCount,
  date,
  slotCapacity,
  capacity,
  onBookingShift,
  handleOpenModal,
  updateBooking,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  draggedBookingDuration,
  isShiftable,
  isLoadingShiftTimes,
}) {
  const calcAttendance = useCallback(
    bookingsList => {
      const cap = Number(entertainment?.laneCount ?? slotCapacity) || 0;
      const densityRaw = Number(entertainment?.participantLaneDensity);
      const participantLaneDensity = Number.isFinite(densityRaw) && densityRaw > 0 ? densityRaw : 1;
      const items = Array.isArray(bookingsList) ? bookingsList : [];

      const times = Array.from({ length: (24 - 9) * 2 + 1 }, (_, index) => {
        const hour = 9 + Math.floor(index / 2);
        const minute = index % 2 === 0 ? 0 : 30;
        return {
          isFull: index % 2 === 0,
          time: `${hour}:${String(minute).padStart(2, '0')}`,
          qualifiedTime: new Date(date.get('year'), date.get('month'), date.get('date'), hour, minute),
          i: index,
          s0: 0,
          frees0: cap,
        };
      });

      for (const bk of items) {
        const bookingType = bk?.bookingType;
        const startKey = bookingType === 'RegiondoBooking' ? 'eventStart' : 'startTime';
        const endKey = bookingType === 'RegiondoBooking' ? 'eventEnd' : 'endTime';

        const compStart = new Date(bk?.[startKey]);
        let compEnd = new Date(bk?.[endKey]);

        // Legacy Regiondo bookings may not have `eventEnd`; fall back to `eventDurationMS`.
        if (bookingType === 'RegiondoBooking' && Number.isNaN(compEnd.getTime())) {
          const durMs = Number(bk?.eventDurationMS);
          if (Number.isFinite(durMs) && durMs > 0 && !Number.isNaN(compStart.getTime())) {
            compEnd = new Date(compStart.getTime() + durMs);
          }
        }
        if (Number.isNaN(compStart.getTime()) || Number.isNaN(compEnd.getTime())) continue;

        const isBlocked = bk?.status === 'blocked';
        const regiondoLaneCountRaw = bk?.laneCount ?? bk?.participantCount;
        const regiondoLaneCount = Number(regiondoLaneCountRaw);

        const internalLaneCountRaw = bk?.laneCount;
        const internalLaneCount = Number(internalLaneCountRaw);
        const internalParticipantsRaw = bk?.participantCount ?? bk?.groupParticipantCount;
        const internalParticipantCount = Number(internalParticipantsRaw);

        // Blockers normally carry the lanes they block in laneCount. Older API responses may
        // only include participantCount, which represents lanes multiplied by lane density.
        const blockerLaneCount =
          Number.isFinite(internalLaneCount) && internalLaneCount > 0
            ? internalLaneCount
            : Number.isFinite(internalParticipantCount) && internalParticipantCount > 0
              ? Math.ceil(internalParticipantCount / participantLaneDensity)
              : cap;

        const usedLanes = isBlocked
          ? blockerLaneCount
          : bookingType === 'RegiondoBooking'
            ? Number.isFinite(regiondoLaneCount) && regiondoLaneCount > 0
              ? regiondoLaneCount
              : 1
            : Number.isFinite(internalLaneCount) && internalLaneCount > 0
              ? internalLaneCount
              : Number.isFinite(internalParticipantCount) && internalParticipantCount > 0
                ? Math.ceil(internalParticipantCount / participantLaneDensity)
                : 1;

        for (const t of times) {
          if (compStart <= t.qualifiedTime && compEnd > t.qualifiedTime) {
            if (!isBlocked) {
              t.s0 += usedLanes;
            }
            t.frees0 -= usedLanes;
          }
        }
      }

      return times;
    },
    [date, entertainment?.laneCount, entertainment?.participantLaneDensity, slotCapacity]
  );

  return (
    <BookingGridBase
      bookings={bookings}
      rowCount={rowCount}
      date={date}
      slotCapacity={slotCapacity}
      calcAttendance={calcAttendance}
      capacityData={capacity}
      availableShiftTimes={availableShiftTimes}
      draggedBookingDuration={draggedBookingDuration}
      isLoadingShiftTimes={isLoadingShiftTimes}
      renderTimeline={({ times, slotWidthPx, showTimeRow, showSlotRows, hideTimeRow }) => (
        <TimeLineAxe
          times={times}
          renderAvailableSlots={true}
          slotWidth={slotWidthPx}
          showTimeRow={showTimeRow}
          showSlotRows={showSlotRows}
          hideTimeRow={hideTimeRow}
        />
      )}
      renderBookingCard={(booking, idx, rowHeight) =>
        booking.bookingType === 'RegiondoBooking' ? (
          <BookingCardAxe key={booking._id} booking={booking} renderAvailableSlots={true} />
        ) : (
          <BookingCardAxev2
            key={booking._id}
            booking={booking}
            handleOpenModal={handleOpenModal}
            renderAvailableSlots={true}
            rowHeight={rowHeight}
            onBookingShift={onBookingShift}
            entertainment={entertainment}
            onDragStart={onDragStart}
            onDragActivated={onDragActivated}
            onDragEnd={onDragEnd}
            availableShiftTimes={availableShiftTimes}
            isShiftable={isShiftable}
          />
        )
      }
      isInternalBookingType={true}
    />
  );
}

export default memo(GridAxe);
